/**
 * Tracking wrapper sobre GTM dataLayer com consent gate LGPD.
 *
 * Fluxo:
 * - Eventos disparados antes do consent ficam em fila in-memory.
 * - Ao consentir analytics/marketing → flush + GTM passa estado para "granted".
 * - GTM tags (GA4, Google Ads, Meta Pixel) configuradas no painel GTM consomem o dataLayer.
 *
 * Não importa nada de React aqui, pode ser chamado de qualquer lugar.
 */

import { readAttribution } from "./utm";
import { firePlatformEvents } from "./analytics";

export type ConsentCategory = "analytics" | "marketing" | "necessary";

export type ConsentState = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  decided: boolean;
  version: number;
  updatedAt: string;
};

export type TrackEventName =
  | "page_view"
  | "simulator_started"
  | "simulator_completed"
  | "whatsapp_clicked"
  | "instagram_clicked"
  | "phone_clicked"
  | "form_submitted"
  | "form_validation_error"
  | "form_submission_failed"
  | "scroll_75"
  | "faq_opened"
  | "vertical_lp_view"
  | "lead_dialog_opened"
  | "cta_click"
  | "consent_granted"
  | "consent_revoked"
  | "web_vital_lcp"
  | "web_vital_cls"
  | "web_vital_fcp"
  | "web_vital_inp";

export type TrackPayload = Record<string, unknown>;

type QueuedEvent = { event: TrackEventName; payload: TrackPayload };

const queue: QueuedEvent[] = [];

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  }
}

const REQUIRES_CONSENT: Record<TrackEventName, ConsentCategory> = {
  page_view: "analytics",
  simulator_started: "analytics",
  simulator_completed: "analytics",
  whatsapp_clicked: "marketing",
  instagram_clicked: "marketing",
  phone_clicked: "marketing",
  form_submitted: "marketing",
  form_validation_error: "analytics",
  form_submission_failed: "analytics",
  scroll_75: "analytics",
  faq_opened: "analytics",
  vertical_lp_view: "analytics",
  lead_dialog_opened: "analytics",
  cta_click: "analytics",
  consent_granted: "necessary",
  consent_revoked: "necessary",
  web_vital_lcp: "analytics",
  web_vital_cls: "analytics",
  web_vital_fcp: "analytics",
  web_vital_inp: "analytics",
};

let currentConsent: Pick<ConsentState, "analytics" | "marketing"> = {
  analytics: false,
  marketing: false,
};

export const setConsentState = (
  next: Pick<ConsentState, "analytics" | "marketing">
) => {
  currentConsent = next;
  pushConsentUpdate(next);
  flushQueue();
};

const isAllowed = (event: TrackEventName): boolean => {
  const cat = REQUIRES_CONSENT[event];
  if (cat === "necessary") return true;
  return currentConsent[cat] === true;
};

const ensureDataLayer = (): Array<Record<string, unknown>> => {
  if (typeof window === "undefined") return [];
  window.dataLayer = window.dataLayer ?? [];
  return window.dataLayer;
};

const pushConsentUpdate = (next: {
  analytics: boolean;
  marketing: boolean;
}) => {
  const dl = ensureDataLayer();
  dl.push({
    event: "consent_update",
    consent: {
      ad_storage: next.marketing ? "granted" : "denied",
      ad_user_data: next.marketing ? "granted" : "denied",
      ad_personalization: next.marketing ? "granted" : "denied",
      analytics_storage: next.analytics ? "granted" : "denied",
    },
  });
};

const flushQueue = () => {
  const dl = ensureDataLayer();
  for (let i = queue.length - 1; i >= 0; i--) {
    const item = queue[i];
    if (isAllowed(item.event)) {
      dl.push({ event: item.event, ...item.payload });
      firePlatformEvents(item.event, item.payload);
      queue.splice(i, 1);
    }
  }
};

export const track = (event: TrackEventName, payload: TrackPayload = {}) => {
  const attribution = readAttribution();
  const enriched: TrackPayload = {
    ...payload,
    ...(Object.keys(attribution).length > 0 ? { attribution } : {}),
    ts: Date.now(),
  };

  if (!isAllowed(event)) {
    queue.push({ event, payload: enriched });
    return;
  }
  const dl = ensureDataLayer();
  dl.push({ event, ...enriched });
  firePlatformEvents(event, enriched);
};
